import { nanoid } from 'nanoid';
import { forEachObjIndexed, omit, sum } from 'ramda';
import type { Location, NodeEntry, NodeMatch, Point } from 'slate';
import { Editor, Node, Path, Range, Transforms } from 'slate';

import type { Length } from '../../types';
import { arrayEntries, pxToIn, pxToTwips } from '../../util';
import type {
  InsertTableColumnLocation,
  InsertTableRowLocation,
  ParagraphElement,
  PartialTableBorders,
  RemoveTableCellEdit,
  RemoveTableRowEdit,
  SuggestedTableCellEdit,
  SuggestedTableRowEdit,
  SuggestionSource,
  TableBorder,
  TableBorders,
  TableCellElement,
  TableCellProps,
  TableElement,
  TableProps,
  TableRowElement,
  TextProps,
} from '../internal';
import {
  AlineTransforms,
  applySuggestions,
  createDefaultParagraph,
  createDefaultTableCellElement,
  createDefaultTableElement,
  createDefaultTableRowElement,
  createDefaultText,
  EditingMode,
  generateLocationId,
  generateSuggestedEditGroupId,
  isInsertTableCellEdit,
  isParagraphElement,
  isRenderModelTable,
  isTableCellElement,
  isTableElement,
  isTableEntry,
  isTableRowElement,
  Queries,
} from '../internal';

interface NewTableProps {
  columnCount: number;
  rowCount: number;
  tableBorders?: TableBorders;
}

interface RowInsertionIndices {
  /**
   * Cell index of a horizontally merged cell that contains the insertion column within its span.
   * When set, the cell's columnSpan should be increased instead of inserting new cells.
   */
  horizontallMergedCellIndex: number | null;
  /**
   * Cell index in the row where new cells should be inserted. Defaults to the end of the row.
   */
  insertionCellIndex: number;
  /**
   * Cell index that covers the source column, used as a template for styling.
   * Null if no cell in the row covers the source column.
   */
  sourceCellIndex: number | null;
}

function findRowInsertionIndices(
  editor: Editor,
  [row, rowPath]: NodeEntry<TableRowElement>,
  insertionIndex: number,
  sourceIndex: number
): RowInsertionIndices {
  let currentColumn = 0;
  let insertionCellIndex = row.children.length;
  let horizontallyMergedCellIndex: number | null = null;
  let sourceCellIndex: number | null = null;

  for (const [cellIndex, cell] of arrayEntries(row.children)) {
    const cellEndColumn = Queries.getTableCellEndColumn(editor, [cell, [...rowPath, cellIndex]]);
    if (sourceCellIndex === null && sourceIndex >= currentColumn && sourceIndex <= cellEndColumn)
      sourceCellIndex = cellIndex;

    if (insertionIndex <= currentColumn) {
      insertionCellIndex = cellIndex;
      break;
    }

    if (insertionIndex > currentColumn && insertionIndex <= cellEndColumn) {
      horizontallyMergedCellIndex = cellIndex;
      break;
    }

    currentColumn = cellEndColumn + 1;
  }

  return {
    horizontallMergedCellIndex: horizontallyMergedCellIndex,
    insertionCellIndex,
    sourceCellIndex,
  };
}

interface TemplateCellResult {
  cellProps: Partial<TableCellProps>;
  paraProps: Partial<ParagraphElement> | undefined;
  textProps: Partial<TextProps> | undefined;
}

function getTemplateCellProps(
  templateCell: TableCellElement,
  rowPath: Path,
  templateCellIndex: number,
  options: { copyVerticalMerge: boolean }
): TemplateCellResult {
  const firstParagraphEntry = Queries.getParagraphEntryInRoot<ParagraphElement>([
    templateCell,
    [...rowPath, templateCellIndex],
  ]);

  let paraProps: Partial<ParagraphElement> | undefined = undefined;
  let textProps: Partial<TextProps> | undefined = undefined;
  if (firstParagraphEntry) {
    const [paragraph] = firstParagraphEntry;

    const { children, suggestedEdits, ...restParagraphProps } = paragraph;
    paraProps = restParagraphProps;

    const [firstText] = Node.texts(paragraph).next().value ?? [];
    textProps = firstText ? Queries.getActiveTextProps(firstText) : undefined;
  }

  const renderedCell = applySuggestions(templateCell);
  const cellProps: Partial<TableCellProps> = {};
  if (renderedCell.borders) cellProps.borders = renderedCell.borders;
  if (renderedCell.margins) cellProps.margins = renderedCell.margins;
  if (renderedCell.shade) cellProps.shade = renderedCell.shade;
  if (options.copyVerticalMerge && renderedCell.verticalMerge)
    cellProps.verticalMerge = renderedCell.verticalMerge;

  return { cellProps, paraProps, textProps };
}

function createInsertedCells(
  count: number,
  width: TableCellElement['width'],
  template: TemplateCellResult,
  options: {
    authorId: string;
    editingMode: EditingMode;
    groupId?: string;
  }
): TableCellElement[] {
  const { authorId, editingMode, groupId } = options;
  const suggestedEdits: SuggestedTableCellEdit[] = [];
  if (editingMode === EditingMode.SUGGESTING) {
    suggestedEdits.push({
      authorId,
      groupId,
      type: 'insertTableCell',
      value: {},
    });
  }

  return Array.from({ length: count }, () => ({
    ...createDefaultTableCellElement({
      paraProps: template.paraProps,
      textProps: template.textProps,
      width,
    }),
    ...template.cellProps,
    suggestedEdits,
  }));
}

const TableTransforms = {
  /**
   * Deletes the table containing the selection, taking into account editing mode.
   */
  deleteTable: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      at?: Location;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode } = options;
      if (!at) return;

      const tableEntry = Queries.atOrAbove(editor, { at, match: isTableElement });
      if (!tableEntry) return;

      const [table, tablePath] = tableEntry;

      if (editingMode === EditingMode.SUGGESTING) {
        const removeRowEdit: RemoveTableRowEdit = {
          authorId,
          createdAt: new Date().toISOString(),
          groupId: nanoid(),
          type: 'removeTableRow',
          value: {},
        };

        for (const [rowIndex, row] of arrayEntries(table.children)) {
          const newSuggestedEdits = Queries.newEdits(row, removeRowEdit);
          if (newSuggestedEdits) {
            Transforms.setNodes(
              editor,
              { suggestedEdits: newSuggestedEdits },
              { at: [...tablePath, rowIndex] }
            );
          }
        }
        return;
      }

      Transforms.removeNodes(editor, { at: tablePath });

      // Move cursor to after the deleted table (tablePath is now the path after the the deleted table).
      if (Node.has(editor, tablePath))
        AlineTransforms.select(editor, Editor.start(editor, tablePath));
    });
  },

  /**
   * Deletes table columns which intersect the selection, taking into account editing mode.
   */
  deleteTableColumns: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      at?: Location;
      /**
       * Overrides the generated group id — pass the server-generated groupId
       * when the deletion is an AI suggestion so the chat card, marks, and
       * suggestion markers share one identifier.
       */
      groupId?: string;
      /**
       * Present when the columns are removed by an AI chat suggestion; stamped
       * onto the cell and content marks so source-based accept/reject resolves
       * the whole group.
       */
      source?: SuggestionSource;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, source } = options;
      if (!at) return;

      const tableEntry = Editor.above(editor, { at, match: isTableElement });
      if (!tableEntry) return;
      const [, tablePath] = tableEntry;

      const selectedCellsInfo = Queries.getTableCellsInSelectedColumns(editor, { at });
      if (!selectedCellsInfo) return;
      const { endColumnIndex, startColumnIndex, tableCellEntries } = selectedCellsInfo;

      if (editingMode === EditingMode.SUGGESTING) {
        const groupId = options.groupId ?? nanoid();
        const removeCellEdit: RemoveTableCellEdit = {
          authorId,
          createdAt: new Date().toISOString(),
          groupId,
          source,
          type: 'removeTableCell',
          value: {},
        };

        for (const [tableCell, tableCellPath] of tableCellEntries.toReversed()) {
          const newSuggestedEdits = Queries.newEdits(tableCell, removeCellEdit);
          if (newSuggestedEdits) {
            Transforms.setNodes(
              editor,
              { suggestedEdits: newSuggestedEdits },
              { at: tableCellPath }
            );
          }

          AlineTransforms.suggestDeletionOfDescendants(editor, {
            at: tableCellPath,
            authorId,
            groupId,
            source,
          });
        }
        return;
      }

      /**
       * The render model is only needed for the direct-edit width math below,
       * and only client editors carry one — keeping this lookup out of the
       * suggesting path lets server-side (AI suggestion) editors delete
       * columns too.
       */
      const [rMTable] = Queries.renderModelNodeFromCMLocation(editor, tablePath);
      if (!isRenderModelTable(rMTable)) throw new Error('Expected render model table');
      const { columnWidthsPx } = rMTable;

      for (const [, tableCellPath] of tableCellEntries.toReversed()) {
        Transforms.removeNodes(editor, { at: tableCellPath });
      }

      const previousTotalWidthPx = sum(columnWidthsPx);
      const deletedColumnsWidthPx = sum(columnWidthsPx.slice(startColumnIndex, endColumnIndex + 1));
      const newTableWidth = Math.round(pxToTwips(previousTotalWidthPx - deletedColumnsWidthPx));

      AlineTransforms.setTableProps(editor, {
        at: tablePath,
        authorId,
        editingMode,
        props: { width: { type: 'tw', value: newTableWidth } },
      });

      AlineTransforms.normalizeTableWidths(editor, {
        authorId,
        editingMode,
        path: tablePath,
      });

      // Select the top most cell after the deleted columns.
      const firstCellEntry = tableCellEntries.at(0);
      if (firstCellEntry && Node.has(editor, firstCellEntry[1])) {
        AlineTransforms.select(editor, Editor.start(editor, firstCellEntry[1]));
      }
    });
  },

  /**
   * Deletes content in a table that is selected via a Range.
   */
  deleteTableContent: (
    editor: Editor,
    options: { range: Range; cellEntry?: NodeEntry<TableCellElement> }
  ): Range => {
    return AlineTransforms.withoutNormalizing<Range>(editor, () => {
      const { cellEntry, range } = options;
      const [start, end] = Range.edges(range);
      const startTableEntry = Editor.above(editor, { at: start, match: isTableElement });
      const endTableEntry = Editor.above(editor, { at: end, match: isTableElement });

      const tablePaths: Path[] = [];
      if (startTableEntry) tablePaths.push(startTableEntry[1]);
      if (endTableEntry && !tablePaths.some(tablePath => Path.equals(tablePath, endTableEntry[1])))
        tablePaths.push(endTableEntry[1]);

      /**
       * Find all rows and convert them to array first.
       * Because when using generator Editor.nodes with reverse:true it will be causing issues
       * in the loop below, because the generator will try to descend into children after
       * Transforms.removeNodes has already deleted that row ()
       */
      const rowEntries = Editor.nodes(editor, { at: range, match: isTableRowElement })
        .filter(([, rowPath]) => tablePaths.some(tablePath => Path.isChild(rowPath, tablePath)))
        .toArray();

      const returnRange: Range = { ...range };
      for (const [, rowPath] of rowEntries.toReversed()) {
        // If w/in a cell and the row is not a descendant of the cell, bail.
        if (cellEntry && !Path.isDescendant(rowPath, cellEntry[1])) continue;

        const rowRange = Editor.range(editor, rowPath);
        const pointAfter = Editor.after(editor, rowRange);
        const pointBefore = Editor.before(editor, rowRange);
        if (Range.includes(rowRange, returnRange.anchor)) {
          const point: Point | undefined = Range.isForward(returnRange) ? pointAfter : pointBefore;
          if (point) returnRange.anchor = point;
        }
        if (Range.includes(rowRange, returnRange.focus)) {
          const point: Point | undefined = Range.isForward(returnRange) ? pointBefore : pointAfter;
          if (point) returnRange.focus = point;
        }

        Transforms.removeNodes(editor, { at: rowPath });
      }

      return returnRange;
    });
  },

  /**
   * Deletes table rows which intersect the selection, taking into account editing mode.
   */
  deleteTableRows: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      at?: Location;
      /**
       * Overrides the generated group id — pass the server-generated groupId
       * when the deletion is an AI suggestion so the chat card, marks, and
       * suggestion markers share one identifier.
       */
      groupId?: string;
      /**
       * Present when the rows are removed by an AI chat suggestion; stamped
       * onto the row and content marks so source-based accept/reject resolves
       * the whole group.
       */
      source?: SuggestionSource;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, source } = options;
      if (!at) return;

      const { rowEntries } = Queries.getContiguousTableRows(editor, { at });
      if (!rowEntries.length) return;

      const [[, firstPath]] = rowEntries;
      const prevParagraphEntry = Editor.previous(editor, {
        at: firstPath,
        match: isParagraphElement,
      });

      if (editingMode === EditingMode.SUGGESTING) {
        const groupId = options.groupId ?? nanoid();
        const removeRowEdit: RemoveTableRowEdit = {
          authorId,
          createdAt: new Date().toISOString(),
          groupId,
          source,
          type: 'removeTableRow',
          value: {},
        };

        rowEntries.forEach(([row, deletePath]) => {
          const newSuggestedEdits = Queries.newEdits(row, removeRowEdit);
          if (newSuggestedEdits) {
            Transforms.setNodes(editor, { suggestedEdits: newSuggestedEdits }, { at: deletePath });
          }

          AlineTransforms.suggestDeletionOfDescendants(editor, {
            at: deletePath,
            authorId,
            groupId,
            source,
          });
        });
        return;
      }
      // Collect the paths to delete in reverse so that they don't change mid-deletion
      const rowDeletionPaths = rowEntries.map(([, rowPath]) => rowPath).reverse();
      rowDeletionPaths.forEach(deletePath => Transforms.removeNodes(editor, { at: deletePath }));

      if (prevParagraphEntry) {
        const [, prevParagraphPath] = prevParagraphEntry;
        AlineTransforms.select(editor, Editor.end(editor, prevParagraphPath));
      }
    });
  },

  /**
   * Resize a table so that it exactly fits inside its parent container.
   */
  fitTableToContainer: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      at?: Location;
    }
  ): void => {
    const { at = editor.selection, authorId, editingMode } = options;
    if (!at) return;

    const tableEntry = Queries.atOrAbove(editor, { at, match: isTableElement });
    if (!tableEntry) return;
    const [table, tablePath] = tableEntry;

    const leftBorderWidth = Queries.getTableMaximumBorderWidth(table, 'left', 'tw');
    const rightBorderWidth = Queries.getTableMaximumBorderWidth(table, 'right', 'tw');

    const containerContentWidth = pxToTwips(
      Queries.getContainerContentWidthPx(editor, { at: tablePath })
    );

    const width = containerContentWidth - (leftBorderWidth + rightBorderWidth) / 2;

    AlineTransforms.setTableProps(editor, {
      at: tablePath,
      authorId,
      editingMode,
      props: {
        indent: { type: 'nil' },
        width: { type: 'tw', value: width },
      },
    });
  },

  /**
   * Inserts a table at the provided Range, taking into account editing mode.
   *
   * TODO: If changing this method, consider adding calls to AlineTransforms.announce to improve
   * screen reader support as per EDI-397.
   */
  insertTable: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      newTableProps: NewTableProps;
      at?: Location;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, newTableProps } = options;
      if (!at) return;

      const suggestionGroupId =
        editingMode === EditingMode.SUGGESTING ? generateSuggestedEditGroupId() : undefined;

      AlineTransforms.insertContentNodes(editor, {
        at,
        authorId,
        editingMode,
        nodes: [
          createDefaultTableElement({
            ...newTableProps,
            authorId,
            contentWidthIn: pxToIn(
              // TODO: No need to use Editor.parent once getRMPathsFromCMPath supports text nodes
              // https://linear.app/aline/issue/EDI-251/migrate-from-relativepath-to-relativeindex
              Queries.getContainerContentWidthPx(editor, { at: Editor.parent(editor, at)[1] })
            ),
            editingMode,
            groupId: suggestionGroupId,
          }),
        ],
        processEdits: false,
        select: 'start',
        suggestionGroupId,
      });
    });
  },

  /**
   * Inserts table columns to the right or left of selected table cells from
   * a single table. Takes into account vertically and horizontally merged cells,
   * cell properties, first paragraph properties in cell
   */
  insertTableColumns: (
    editor: Editor,
    options: {
      authorId: string;
      columnLocation: InsertTableColumnLocation;
      editingMode: EditingMode;
      at?: Location;
      numInsertedCols?: number;
      select?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const {
        at = editor.selection,
        authorId,
        columnLocation,
        editingMode,
        numInsertedCols,
        select = true,
      } = options;

      if (!at) return;

      const groupId = editingMode === EditingMode.SUGGESTING ? nanoid() : undefined;

      const initialTableEntry = Editor.above(editor, { at, match: isTableElement });
      if (!initialTableEntry) return;

      AlineTransforms.normalizeTableWidths(editor, {
        authorId,
        editingMode,
        groupId,
        path: initialTableEntry[1],
      });

      const tableEntry = Editor.above(editor, { at, match: isTableElement });
      if (!tableEntry) return;

      const [table, tablePath] = tableEntry;

      const cellEntries = Queries.getContiguousTableCells(editor, { at });
      if (!cellEntries.length) return;

      const [firstCellEntry] = cellEntries;
      const lastCellEntry = cellEntries[cellEntries.length - 1];
      if (!firstCellEntry || !lastCellEntry) return;

      const startColumnIndex = Queries.getTableCellStartColumn(editor, firstCellEntry);
      const endColumnIndex = Queries.getTableCellEndColumn(editor, lastCellEntry);
      const insertedColumnsCount = numInsertedCols ?? cellEntries.length;
      if (insertedColumnsCount < 1) return;

      const sourceColumnIndex = columnLocation === 'left' ? startColumnIndex : endColumnIndex;
      const insertionColumnIndex =
        columnLocation === 'left' ? startColumnIndex : endColumnIndex + 1;

      // gridColumns should be always present and correct after width normalization
      const { gridColumns } = applySuggestions(table);
      if (!gridColumns) throw new Error('Expected gridColumns');

      const insertedCellWidth = gridColumns.at(sourceColumnIndex);
      if (insertedCellWidth === undefined)
        throw new Error('Expected gridColumns to include sourceColumnIndex');

      let firstSelectionPath: Path | null = null;

      for (const [rowIndex, row] of arrayEntries(table.children)) {
        if (!row.children.length) continue;

        const rowPath = [...tablePath, rowIndex];

        const { horizontallMergedCellIndex, insertionCellIndex, sourceCellIndex } =
          findRowInsertionIndices(editor, [row, rowPath], insertionColumnIndex, sourceColumnIndex);

        if (horizontallMergedCellIndex !== null) {
          const horizontallyMergedCellPath = [...rowPath, horizontallMergedCellIndex];
          const horizontallyMergedCell = row.children[horizontallMergedCellIndex];
          if (!horizontallyMergedCell) continue;
          const currentColumnSpan = Queries.renderedTableCellColumnSpan(horizontallyMergedCell);

          AlineTransforms.setTableCellProps(editor, {
            at: horizontallyMergedCellPath,
            authorId,
            editingMode,
            groupId,
            props: {
              columnSpan: currentColumnSpan + insertedColumnsCount,
            },
          });

          if (!firstSelectionPath) firstSelectionPath = horizontallyMergedCellPath;
          continue;
        }

        const fallbackTemplateCellIndex =
          columnLocation === 'left'
            ? Math.min(insertionCellIndex, row.children.length - 1)
            : Math.max(0, insertionCellIndex - 1);

        const templateCellIndex = sourceCellIndex ?? fallbackTemplateCellIndex;
        const templateCell = row.children[templateCellIndex];
        if (!templateCell) continue;

        const template = getTemplateCellProps(templateCell, rowPath, templateCellIndex, {
          copyVerticalMerge: sourceCellIndex !== null,
        });

        const insertedCells = createInsertedCells(
          insertedColumnsCount,
          insertedCellWidth,
          template,
          { authorId, editingMode, groupId }
        );

        const insertionPath = [...rowPath, insertionCellIndex];
        Transforms.insertNodes(editor, insertedCells, { at: insertionPath });

        if (!firstSelectionPath) firstSelectionPath = insertionPath;
      }

      /**
       * Set the table's preferred width to auto. This ensures that for autofit tables, the table
       * will not exceed the width of its container.
       */
      AlineTransforms.setTableProps(editor, {
        at: tablePath,
        authorId,
        editingMode,
        groupId,
        props: { width: { type: 'auto' } },
      });

      AlineTransforms.normalizeTableWidths(editor, {
        authorId,
        editingMode,
        groupId,
        path: tablePath,
      });

      if (select && firstSelectionPath && Node.has(editor, firstSelectionPath))
        AlineTransforms.select(editor, Editor.start(editor, firstSelectionPath));
    });
  },

  /**
   * Inserts table rows above or below the row(s) of the cursor, taking into account editing mode.
   */
  insertTableRows: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      rowLocation: InsertTableRowLocation;
      at?: Location;
      numInsertedRows?: number;
      select?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const {
        at = editor.selection,
        authorId,
        editingMode,
        numInsertedRows,
        rowLocation,
        select = true,
      } = options;
      if (!at) return;

      const { rowEntries, tableEntry } = Queries.getContiguousTableRows(editor, { at });
      if (!tableEntry || !rowEntries.length) return;

      const lastRowEntry = rowEntries[rowEntries.length - 1];
      const [firstRowEntry] = rowEntries;
      const [row, rowPath] = rowLocation === 'above' ? firstRowEntry : lastRowEntry;
      const cellDefinitions = row.children.map((cell, index) => {
        const cellPath = [...rowPath, index];
        const firstParagraphEntry = Queries.getParagraphEntryInRoot<ParagraphElement>([
          cell,
          cellPath,
        ]);
        if (!firstParagraphEntry)
          return {
            inline: createDefaultText(),
            paragraph: createDefaultParagraph(),
            width: cell.width,
          };

        const [paragraph] = firstParagraphEntry;
        const [inline] = paragraph.children;
        return { inline, paragraph, width: cell.width };
      });
      const columnCount = row.children.length;
      const insertionPath = rowLocation === 'above' ? rowPath : Path.next(rowPath);
      const suggestedEdits: SuggestedTableRowEdit[] = [];

      if (editingMode === EditingMode.SUGGESTING) {
        suggestedEdits.push({
          authorId,
          groupId: nanoid(),
          type: 'insertTableRow',
          value: {},
        });
      }

      /**
       * TODO: Respect existing formatting
       * @url https://arity.atlassian.net/browse/AL-1900
       */
      const insertedRows: TableRowElement[] = Array.from(
        { length: numInsertedRows ?? rowEntries.length },
        () => ({
          ...createDefaultTableRowElement({ columnCount }),
          children: cellDefinitions.map(({ inline, paragraph, width }) => ({
            ...createDefaultTableCellElement({ width }),
            children: [
              {
                ...omit(['listLevel', 'listProps'], paragraph),
                children: [{ ...inline, comments: [], suggestedEdits: [], text: '' }],
                // Fresh id per cell; the spread above would otherwise duplicate the template row's locationId.
                locationId: generateLocationId(),
                suggestedEdits: [],
              },
            ],
          })),
          suggestedEdits,
        })
      );

      Transforms.insertNodes(editor, insertedRows, { at: insertionPath });

      if (select) AlineTransforms.select(editor, Editor.start(editor, insertionPath));
    });
  },

  /**
   * Update all table widths (table-level, column-level and cell-level) to match the current render
   * model cell widths.
   */
  normalizeTableWidths: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      path: Path;
      groupId?: string;
    }
  ): void => {
    const { authorId, editingMode, groupId, path } = options;

    const tableEntry = Editor.node(editor, path);
    if (!isTableEntry(tableEntry)) throw new Error('Expected table entry');
    const [table] = tableEntry;

    const [rMTable] = Queries.renderModelNodeFromCMLocation(editor, path);
    if (!isRenderModelTable(rMTable)) throw new Error('Expected render model table');

    const columnWidths = Queries.renderedTableColumnWidths(table, {
      availableWidth: pxToTwips(Queries.getContainerContentWidthPx(editor, { at: path })),
    });
    const gridColumns: Length.Twip[] = columnWidths.map(value => ({ type: 'tw', value }));

    AlineTransforms.withoutNormalizing(editor, () => {
      if (editingMode === EditingMode.SUGGESTING && !table.gridColumns) {
        Transforms.setNodes<TableElement>(editor, { gridColumns }, { at: path });
      }

      AlineTransforms.setTableProps(editor, {
        at: path,
        authorId,
        editingMode,
        groupId,
        props: {
          gridColumns,
          width: { type: 'tw', value: sum(columnWidths) },
        },
      });

      for (const [tableRowIndex, tableRow] of arrayEntries(table.children)) {
        let startColumnIndex = 0;

        for (const [tableCellIndex, tableCell] of arrayEntries(tableRow.children)) {
          const tableCellPath = [...path, tableRowIndex, tableCellIndex];
          const endColumnIndex = Queries.getTableCellEndColumnFromStartColumn(
            tableCell,
            startColumnIndex
          );
          const cellWidth = sum(columnWidths.slice(startColumnIndex, endColumnIndex + 1));

          // When a cell is a suggested insertion, set the width directly
          // instead of creating a tableCellFormat suggestion. The width is
          // part of the insertion itself, not a separate change.
          const isCellInsertSuggestion =
            editingMode === EditingMode.SUGGESTING &&
            tableCell.suggestedEdits?.some(isInsertTableCellEdit);

          if (isCellInsertSuggestion) {
            Transforms.setNodes<TableCellElement>(
              editor,
              { width: { type: 'tw', value: cellWidth } },
              { at: tableCellPath }
            );
          } else {
            AlineTransforms.setTableCellProps(editor, {
              at: tableCellPath,
              authorId,
              editingMode,
              groupId,
              props: { width: { type: 'tw', value: cellWidth } },
            });
          }

          startColumnIndex = endColumnIndex + 1;
        }
      }
    });
  },

  /**
   * Resizes the table columns by the given width delta in twips.
   * @returns The amount in twips by which the column edge was actually moved to the right.
   */
  resizeTableColumns(
    editor: Editor,
    options: {
      authorId: string;
      cMTablePath: Path;
      /**
       * The index of the column immediately to the right of the edge being dragged. If the
       * rightmost edge is being resized, this will equal the number of columns.
       */
      columnEdgeIndex: number;
      editingMode: EditingMode;
      /**
       * The amount in twips by which the column edge should be moved to the right.
       */
      widthDelta: number;
    }
  ): number {
    const { authorId, cMTablePath, columnEdgeIndex, editingMode, widthDelta } = options;

    return AlineTransforms.withoutNormalizing(editor, () => {
      // Ensure that all table widths are in twips before fetching any nodes
      AlineTransforms.normalizeTableWidths(editor, { authorId, editingMode, path: cMTablePath });

      const cMTableEntry = Editor.node(editor, cMTablePath);
      if (!isTableEntry(cMTableEntry)) throw new Error('Expected table entry');
      const [cMTable] = cMTableEntry;

      const columnCount = Queries.getTableColumnCount(cMTable);
      const leftColumnIndex = columnEdgeIndex > 0 ? columnEdgeIndex - 1 : null;
      const rightColumnIndex = columnEdgeIndex < columnCount ? columnEdgeIndex : null;

      const rMTablePath = Queries.getFirstRMPathFromCMPath(editor, cMTablePath);
      if (!rMTablePath) throw new Error('Expected render model path');

      const containerContentWidthPx = Queries.getRMContainerContentWidthPx(editor, {
        rMPath: rMTablePath,
      });
      const { leftMargin: leftMarginPx, rightMargin: rightMarginPx } =
        Queries.getRMContainerMargins(editor, rMTablePath, 'px');
      const containerWidth = pxToTwips(containerContentWidthPx + leftMarginPx + rightMarginPx);

      const leftBorderWidth = Queries.getTableMaximumBorderWidth(cMTable, 'left', 'tw');
      const rightBorderWidth = Queries.getTableMaximumBorderWidth(cMTable, 'right', 'tw');

      // Adjust available width to account for borders rendered outside the table,
      // preventing them from overflowing the container.
      const availableWidth = containerWidth - (leftBorderWidth + rightBorderWidth) / 2;
      const columnWidths = Queries.renderedTableColumnWidths(cMTable, {
        availableWidth,
      });
      const minimumColumnWidths = Queries.getTableMinimumColumnWidths(cMTable);

      const { width } = applySuggestions(cMTable);
      if (!width || typeof width === 'number' || width.type !== 'tw')
        throw new Error('Expected width to be in twips');

      const indent = Queries.renderedTableIndent(cMTable, 'tw');
      const indentForClamping = indent + pxToTwips(leftMarginPx);
      const clampedWidthDelta = getClampedWidthDelta({
        availableWidth,
        columnWidths,
        indent: indentForClamping,
        leftColumnIndex,
        minimumColumnWidths,
        rightColumnIndex,
        tableWidth: width.value,
        widthDelta,
      });

      const tableWidthChange =
        leftColumnIndex === null
          ? -clampedWidthDelta
          : rightColumnIndex === null
            ? clampedWidthDelta
            : 0;

      const resizedColumnWidths = columnWidths.map((columnWidth, columnIndex) => {
        if (columnIndex === leftColumnIndex) return columnWidth + clampedWidthDelta;
        if (columnIndex === rightColumnIndex) return columnWidth - clampedWidthDelta;
        return columnWidth;
      });

      AlineTransforms.setTableProps(editor, {
        at: cMTablePath,
        authorId,
        editingMode,
        props: {
          gridColumns: resizedColumnWidths.map(value => ({ type: 'tw', value })),
          width: { type: 'tw', value: width.value + tableWidthChange },
        },
      });

      function resizeCellsInColumn(columnIndex: number, direction: 'left' | 'right') {
        if (!isTableEntry(cMTableEntry)) throw new Error('Expected table entry');

        const signedWidthDelta = clampedWidthDelta * (direction === 'right' ? -1 : 1);

        for (const {
          endColumnIndex,
          startColumnIndex,
          tableCellEntry,
        } of Queries.getTableCellEntriesInColumn(cMTableEntry, columnIndex)) {
          // Do not resize cells that do not start/end at the column edge
          const startsOrEndsOnColumn = {
            left: endColumnIndex === columnIndex,
            right: startColumnIndex === columnIndex,
          }[direction];
          if (!startsOrEndsOnColumn) continue;

          const [tableCell, tableCellPath] = tableCellEntry;

          const { width } = applySuggestions(tableCell);
          if (!width || typeof width === 'number' || width.type !== 'tw')
            throw new Error('Expected width to be in twips');

          AlineTransforms.setTableCellProps(editor, {
            at: tableCellPath,
            authorId,
            editingMode,
            props: { width: { type: 'tw', value: width.value + signedWidthDelta } },
          });
        }
      }

      if (typeof leftColumnIndex === 'number') {
        resizeCellsInColumn(leftColumnIndex, 'left');
      } else if (clampedWidthDelta !== 0) {
        AlineTransforms.setTableProps(editor, {
          at: cMTablePath,
          authorId,
          editingMode,
          props: {
            indent: {
              type: 'tw',
              value: indent + clampedWidthDelta,
            },
          },
        });
      }

      if (typeof rightColumnIndex === 'number') {
        resizeCellsInColumn(rightColumnIndex, 'right');
      }

      return clampedWidthDelta;
    });
  },

  /**
   * Sets border properties on the selected table cells. When multiple cells are
   * selected, shared borders between adjacent cells are updated appropriately.
   * Only affects cells directly in the table at the given location (not nested
   * table cells).
   */
  setSelectedTableCellsBorder: (
    editor: Editor,
    options: {
      authorId: string;
      borderProps: Partial<TableBorder>;
      editingMode: EditingMode;
      at?: Location;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, borderProps, editingMode } = options;
      if (!at) return;

      const tableEntry = Queries.atOrAbove(editor, { at, match: isTableElement });
      if (!tableEntry) return;

      const [, tablePath] = tableEntry;
      /**
       * With an active grid (table cell rectangle) selection, the affected cells are exactly the
       * rect's cells (including 'continue' ghost cells, which carry their own border props); the
       * linear anchor-to-focus range would also cover cells outside the rectangle.
       */
      const gridCellEntries = Queries.getGridSelectionInfoForLocation(editor, { at })
        ? Queries.getGridSelectionCellEntries(editor, { includeVerticalMergeContinuations: true })
        : null;
      const selectedCellEntries: NodeEntry<TableCellElement>[] =
        gridCellEntries ??
        Editor.nodes(editor, {
          at,
          match: isTableCellElement,
        })
          .filter(
            // Filter to avoid setting border props for parent cells, when modifying nested table cells
            ([, cellPath]) =>
              Path.isAncestor(tablePath, cellPath) && cellPath.length === tablePath.length + 2
          )
          .toArray();
      if (selectedCellEntries.length === 0) return;

      const borderUpdates = createBorderUpdates(editor, {
        borderProps,
        selectedCellEntries,
        tableEntry,
      });

      for (const { borders, path: cellPath } of borderUpdates) {
        const [cell] = Editor.node(editor, cellPath);
        if (!isTableCellElement(cell)) continue;

        const existingBorders = cell.borders ?? {};
        const mergedBorders: PartialTableBorders = { ...existingBorders };

        forEachObjIndexed((border, direction) => {
          mergedBorders[direction] = { ...existingBorders[direction], ...border };
        }, borders);

        const props: Partial<TableCellProps> = { borders: mergedBorders };

        switch (editingMode) {
          case EditingMode.EDITING: {
            AlineTransforms.removeConflictingSuggestions(editor, {
              editingMode,
              entry: [cell, cellPath],
              props,
            });
            Transforms.setNodes<TableCellElement>(editor, props, { at: cellPath });
            break;
          }
          case EditingMode.SUGGESTING: {
            const nextSuggestedEdits = Queries.newEdits(cell, {
              authorId,
              type: 'tableCellFormat',
              value: props,
            });
            if (!nextSuggestedEdits) break;

            Transforms.setNodes<TableCellElement>(
              editor,
              { suggestedEdits: nextSuggestedEdits },
              { at: cellPath }
            );
            break;
          }
        }
      }
    });
  },

  /**
   * Sets a table with new border properties.
   */
  setTableBorder: (
    editor: Editor,
    options: {
      authorId: string;
      borderProps: Partial<TableBorder>;
      editingMode: EditingMode;
      at?: Location;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, borderProps, editingMode } = options;
      if (!at) return;

      const tableEntry = Queries.atOrAbove(editor, { at, match: isTableElement });
      if (!tableEntry) return;

      const [table, tablePath] = tableEntry;
      const currentBorders = Queries.mergeTableBordersWithDefaults(table);

      const newBorders: TableBorders = {
        bottom: { ...currentBorders.bottom, ...borderProps },
        insideH: { ...currentBorders.insideH, ...borderProps },
        insideV: { ...currentBorders.insideV, ...borderProps },
        left: { ...currentBorders.left, ...borderProps },
        right: { ...currentBorders.right, ...borderProps },
        top: { ...currentBorders.top, ...borderProps },
      };

      AlineTransforms.setTableProps(editor, {
        at: tablePath,
        authorId,
        editingMode,
        props: { borders: newBorders },
      });
    });
  },

  /**
   * Generic function to set any table cell props.
   */
  setTableCellProps: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      props: Partial<TableCellProps>;
      at?: Location;
      groupId?: string;
      match?: NodeMatch<TableCellElement>;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, groupId, match, props } = options;
      if (!at) return;

      /**
       * With an active grid (table cell rectangle) selection, the affected cells are exactly the
       * rect's cells; the linear anchor-to-focus range would also cover cells outside the rectangle.
       * Ghost 'continue' cells join via the vertical-merge group expansion below.
       */
      const gridCellEntries = Queries.getGridSelectionInfoForLocation(editor, { at })
        ? Queries.getGridSelectionCellEntries(editor)?.filter(
            ([cell, cellPath]) => !match || match(cell, cellPath)
          )
        : null;
      const tableCellEntries = (
        gridCellEntries ?? Array.from(Queries.getTableCellEntriesForLocation(editor, { at, match }))
      ).flatMap(cellEntry => Queries.getVerticallyMergedTableCellGroup(editor, cellEntry));

      if (!tableCellEntries.length) return;

      switch (editingMode) {
        case EditingMode.EDITING: {
          for (const [cell, cellPath] of tableCellEntries) {
            AlineTransforms.removeConflictingSuggestions(editor, {
              editingMode,
              entry: [cell, cellPath],
              props,
            });
            Transforms.setNodes<TableCellElement>(editor, props, {
              at: cellPath,
            });
          }
          return;
        }
        case EditingMode.SUGGESTING: {
          for (const [tableCell, tableCellPath] of tableCellEntries) {
            const nextSuggestedEdits = Queries.newEdits(tableCell, {
              authorId,
              groupId,
              type: 'tableCellFormat',
              value: props,
            });
            if (!nextSuggestedEdits) return;

            Transforms.setNodes<TableCellElement>(
              editor,
              { suggestedEdits: nextSuggestedEdits },
              { at: tableCellPath }
            );
          }
          return;
        }
      }
    });
  },

  /**
   * Generic function to set any table props.
   *
   * When setting `align` or `indent`, remember that:
   *
   * - When setting `indent` to a value, `align` must be set to `left`.
   * - When setting 'align' to anything other than `left`, indent must be set to { type: `nil` }
   */
  setTableProps: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      props: Partial<TableProps>;
      at?: Location;
      groupId?: string;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, groupId, props } = options;
      if (!at) return;

      const tableEntry = Queries.atOrAbove(editor, { at, match: isTableElement, mode: 'lowest' });
      if (!tableEntry) return;

      const [table, tablePath] = tableEntry;

      switch (editingMode) {
        case EditingMode.EDITING: {
          /**
           * Set the table's properties.
           */
          Transforms.setNodes<TableElement>(editor, props, { at: tablePath });
          /**
           * If we're modifying the fillColor of the table, Word's behavior is
           * to set the fillColor for each of the table's immediate children.
           */
          if (props.shade?.fillColor) {
            AlineTransforms.setTableCellProps(editor, {
              at: tablePath,
              authorId,
              editingMode,
              match: (node, path) => {
                /**
                 * Looking for the table cell that is two steps deeper than
                 * the table itself so that we don't overcapture cells of a
                 * nested table.
                 */
                return isTableCellElement(node) && path.length === tablePath.length + 2;
              },
              props: { shade: { fillColor: props.shade.fillColor } },
            });
          }
          return;
        }
        case EditingMode.SUGGESTING: {
          /**
           * Suggests the table properties.
           */
          const nextTableEdits = Queries.newEdits<TableElement>(table, {
            authorId,
            groupId,
            type: 'tableFormat',
            value: props,
          });
          if (!nextTableEdits) return;
          Transforms.setNodes<TableElement>(
            editor,
            { suggestedEdits: nextTableEdits },
            { at: tablePath }
          );
          if (props.shade?.fillColor) {
            AlineTransforms.setTableCellProps(editor, {
              at: tablePath,
              authorId,
              editingMode,
              groupId,
              match: (node, path) => {
                /**
                 * Looking for the table cell that is two steps deeper than
                 * the table itself so that we don't overcapture cells of a
                 * nested table.
                 */
                return isTableCellElement(node) && path.length === tablePath.length + 2;
              },
              props: { shade: { fillColor: props.shade.fillColor } },
            });
          }
          return;
        }
      }
    });
  },
};

interface BorderUpdate {
  borders: PartialTableBorders;
  path: Path;
}

function createBorderUpdates(
  editor: Editor,
  options: {
    borderProps: Partial<TableBorder>;
    selectedCellEntries: NodeEntry<TableCellElement>[];
    tableEntry: NodeEntry<TableElement>;
  }
) {
  const { borderProps, selectedCellEntries, tableEntry } = options;
  const [table, tablePath] = tableEntry;

  const borderUpdates = new Map<string, BorderUpdate>();

  for (const cellEntry of selectedCellEntries) {
    addBorderUpdatesForCell(cellEntry);
  }

  return Array.from(borderUpdates.values());

  function addBorderUpdate(cellPath: Path, direction: 'top' | 'bottom' | 'left' | 'right') {
    const key = cellPath.join(',');
    const existing = borderUpdates.get(key);
    const existingBorders = existing?.borders ?? {};
    const mergedBorders = {
      ...existingBorders,
      [direction]: { ...existingBorders[direction], ...borderProps },
    };
    borderUpdates.set(key, { borders: mergedBorders, path: cellPath });
  }

  function addBorderUpdatesForCell(cellEntry: NodeEntry<TableCellElement>) {
    const vmGroup = Queries.getVerticallyMergedTableCellGroup(editor, cellEntry);

    for (let i = 0; i < vmGroup.length; i++) {
      const [, vmCellPath] = vmGroup[i];
      const vmRowIndex = vmCellPath[tablePath.length];
      const vmCellIndex = vmCellPath[tablePath.length + 1];
      const vmCellEntry = vmGroup[i];
      const startCol = Queries.getTableCellStartColumn(editor, vmCellEntry);
      const endCol = Queries.getTableCellEndColumn(editor, vmCellEntry);

      addBorderUpdate(vmCellPath, 'top');
      addBorderUpdate(vmCellPath, 'bottom');
      addBorderUpdate(vmCellPath, 'left');
      addBorderUpdate(vmCellPath, 'right');

      const vmRow = table.children[vmRowIndex];

      const leftNeighbor = vmRow.children[vmCellIndex - 1];
      const rightNeighbor = vmRow.children[vmCellIndex + 1];

      if (leftNeighbor) addBorderUpdate([...tablePath, vmRowIndex, vmCellIndex - 1], 'right');
      if (rightNeighbor) addBorderUpdate([...tablePath, vmRowIndex, vmCellIndex + 1], 'left');

      if (i === 0) {
        for (const [, abovePath] of Queries.getTableCellEntriesInRowByColumnRange(
          tableEntry,
          vmRowIndex - 1,
          { endColumnIndex: endCol, startColumnIndex: startCol }
        )) {
          addBorderUpdate(abovePath, 'bottom');
        }
      }

      if (i === vmGroup.length - 1) {
        for (const [, belowPath] of Queries.getTableCellEntriesInRowByColumnRange(
          tableEntry,
          vmRowIndex + 1,
          { endColumnIndex: endCol, startColumnIndex: startCol }
        )) {
          addBorderUpdate(belowPath, 'top');
        }
      }
    }
  }
}

/**
 * Returns a width delta adjusted so affected columns never go below their minimum widths and
 * the table never goes beyond the page edges (container content width + left margin + right margin).
 */
function getClampedWidthDelta(options: {
  availableWidth: number;
  columnWidths: number[];
  indent: number;
  leftColumnIndex: number | null;
  minimumColumnWidths: number[];
  rightColumnIndex: number | null;
  tableWidth: number;
  widthDelta: number;
}): number {
  const {
    availableWidth,
    columnWidths,
    indent,
    leftColumnIndex,
    minimumColumnWidths,
    rightColumnIndex,
    tableWidth,
    widthDelta,
  } = options;

  if (widthDelta === 0) return 0;

  const isRightEdge = rightColumnIndex === null;
  const isLeftEdge = leftColumnIndex === null;

  let clampedWidthDelta = widthDelta;

  if (widthDelta > 0 && !isRightEdge) {
    const currentWidth = columnWidths[rightColumnIndex] ?? 0;
    const minimumWidth = minimumColumnWidths[rightColumnIndex] ?? 0;
    const maxDelta = currentWidth - minimumWidth;
    if (maxDelta <= 0) return 0;
    clampedWidthDelta = Math.min(widthDelta, maxDelta);
  }

  if (widthDelta < 0 && !isLeftEdge) {
    const currentWidth = columnWidths[leftColumnIndex] ?? 0;
    const minimumWidth = minimumColumnWidths[leftColumnIndex] ?? 0;
    const maxDelta = currentWidth - minimumWidth;
    if (maxDelta <= 0) return 0;
    clampedWidthDelta = Math.max(widthDelta, -maxDelta);
  }

  if (isLeftEdge && clampedWidthDelta < 0) {
    return Math.max(clampedWidthDelta, -indent);
  }

  if (isRightEdge && clampedWidthDelta > 0) {
    const maxTableWidth = availableWidth - indent;
    const maxIncrease = maxTableWidth - tableWidth;
    if (maxIncrease <= 0) return 0;
    return Math.min(clampedWidthDelta, maxIncrease);
  }

  return clampedWidthDelta;
}

export default TableTransforms;
